"use client"

import type { ReactNode } from "react"
import Link from "next/link"

import { usePortalChrome } from "@/components/site/portal-chrome-context"
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import type { PortalNavItem } from "@/lib/portal-content"
import { SITE_NAV, SITE_SERVICES } from "@/lib/site-nav"

function fallbackItems(): PortalNavItem[] {
  return [
    { id: "home", title: SITE_NAV.home.label, href: SITE_NAV.home.href },
    { id: "labels", title: SITE_NAV.labels.label, href: SITE_NAV.labels.href },
    { id: "km", title: SITE_NAV.km.label, href: SITE_NAV.km.href },
    {
      id: "services",
      title: "Сервисы",
      href: "/services",
      children: SITE_SERVICES.map((item, index) => ({
        id: `svc-${index}`,
        title: item.label,
        href: item.href,
      })),
    },
    { id: "support", title: SITE_NAV.support.label, href: SITE_NAV.support.href },
  ]
}

/** Пункты главного меню портала — шапка (mobile) и бургер редактора `/labels`. */
export function PortalNavMenuItems({ items }: { items?: PortalNavItem[] }) {
  const { navigation } = usePortalChrome()
  const tree =
    items ??
    (navigation.length > 0 ? navigation : fallbackItems()).filter(
      (item) => item.is_visible !== false,
    )

  const nodes: ReactNode[] = []

  tree.forEach((item, index) => {
    const children = (item.children ?? []).filter(
      (child) => child.is_visible !== false,
    )
    const isHeavy = item.href.startsWith("/labels") || item.href.startsWith("/km")

    if (children.length > 0) {
      if (index > 0) nodes.push(<DropdownMenuSeparator key={`sep-${item.id}`} />)
      children.forEach((child) => {
        nodes.push(
          <DropdownMenuItem
            key={child.id}
            render={<Link href={child.href} prefetch={false} />}
          >
            {child.title}
          </DropdownMenuItem>,
        )
      })
      if (index < tree.length - 1) {
        nodes.push(<DropdownMenuSeparator key={`sep-after-${item.id}`} />)
      }
      return
    }

    nodes.push(
      <DropdownMenuItem
        key={item.id}
        render={
          isHeavy ? (
            <a href={item.href} />
          ) : (
            <Link href={item.href} prefetch={false} />
          )
        }
      >
        {item.title}
      </DropdownMenuItem>,
    )
  })

  return <>{nodes}</>
}
